import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { captureLeadEmail } from '../utils/captureLeadEmail';

function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = useMemo(() => new URLSearchParams(location.search).get('token'), [location.search]);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [verifying, setVerifying] = useState(true);
  const [validToken, setValidToken] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendStatus, setResendStatus] = useState(null);
  const [resendSubmitting, setResendSubmitting] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        setValidToken(false);
        return;
      }
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/password-reset/verify?token=${encodeURIComponent(token)}`);
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data?.error || 'Invalid or expired token');
        }
        setValidToken(true);
      } catch (err) {
        setError(err.message);
        setValidToken(false);
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleEmailBlur = () => {
    captureLeadEmail(resendEmail, window.location.pathname, { context: 'reset_password' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setStatus(null);

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/password-reset/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || 'Unable to reset password');
      }
      setStatus('Password updated successfully. Redirecting to login...');
      setTimeout(() => navigate('/login'), 1600);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async (e) => {
    e.preventDefault();
    setResendStatus(null);
    setError(null);

    if (!resendEmail) {
      setError('Please enter your email.');
      return;
    }

    setResendSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/password-reset/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resendEmail })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || 'Unable to send reset link');
      }
      setResendStatus(data?.message || 'If an account exists for that email, a reset link has been sent.');
    } catch (err) {
      setError(err.message);
    } finally {
      setResendSubmitting(false);
    }
  };

  if (verifying) {
    return (
      <main className="App-main">
        <h2>Reset your password</h2>
        <p className="status-text">Checking your reset link...</p>
      </main>
    );
  }

  if (!token || !validToken) {
    return (
      <main className="App-main">
        <h2>Reset link expired</h2>
        <p className="error-message">{error || 'This reset link is invalid or expired.'}</p>
        <form onSubmit={handleResend} className="auth-form" style={{ marginTop: '1rem' }}>
          {resendStatus && <p className="success-message">{resendStatus}</p>}
          <div className="form-group">
            <label htmlFor="resendEmail">Email</label>
            <input
              type="email"
              id="resendEmail"
              value={resendEmail}
              onChange={(e) => setResendEmail(e.target.value)}
              onBlur={handleEmailBlur}
              required
            />
          </div>
          <button type="submit" className="primary-button" disabled={resendSubmitting}>
            {resendSubmitting ? 'Sending...' : 'Send a new reset link'}
          </button>
        </form>
        <p style={{ marginTop: '1rem' }}><Link to="/login">Back to login</Link></p>
      </main>
    );
  }

  return (
    <main className="App-main">
      <h2>Choose a new password</h2>
      <form onSubmit={handleSubmit} className="auth-form">
        {error && <p className="error-message">{error}</p>}
        {status && <p className="success-message">{status}</p>}
        <div className="form-group">
          <label htmlFor="password">New password</label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="confirm">Confirm password</label>
          <input
            type="password"
            id="confirm"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
        </div>
        <button type="submit" className="primary-button" disabled={submitting}>
          {submitting ? 'Updating...' : 'Update password'}
        </button>
      </form>
      <p style={{ marginTop: '1rem' }}>
        <Link to="/login">Back to login</Link>
      </p>
    </main>
  );
}

export default ResetPassword;
